// Imports
import { faPencil } from '@fortawesome/pro-regular-svg-icons';
import { faPlus, faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import styled from 'styled-components/macro';
import Button from '../../../../../../../shareables/foundation/front-end/components/buttons/standard';
import LoadingRow from '../../../../../../../shareables/foundation/front-end/components/tables/loading-row';
import { useAppDispatch, useAppSelector } from '../../../../../../../shareables/foundation/front-end/redux/hooks';
import helperFunctions from '../../../../../../../shareables/foundation/front-end/utils/helper-functions';
import APIButton from '../../../../../../../shareables/integrations/api/components/button';
import apiActions from '../../../../../../../shareables/integrations/api/redux/actions';
import { GetMembersResponse } from '../../../../../../../shareables/types/api/main/website/account/members/get';
import { GetRegistrationResponse } from '../../../../../../../shareables/types/api/main/website/settings/registration/get';
import UserAvatar from '../../../../../shared-components/user-avatar';
import Table from '../../../page-types/shared-components/table';
import UpsertMember from '../../shared-components/upsert-member/upsert-member';


// Styled components
const MemberCell = styled.div`
	display: flex;
	align-items: center;
	gap: 1rem;
`;

const Image = styled.img`
	width: 3.125rem;
	height: 3.125rem;
	
	border-radius: 50%;
`;

const ProfileAvatar = styled(UserAvatar)`
	align-items: center;
	justify-content: center;
	font-size: 1.25rem;
	
	width: 3.125rem;
	height: 3.125rem;
`;

const NameContainer = styled.p`
	line-height: 1.1;
`;

const Email = styled.span`
	font-size: 75%;
`;

const CannotDeleteContainer = styled.p`
	font-size: smaller;
	line-height: 1.1;
`;

const EditMemberButton = styled(Button)`
	border: 0.0625rem solid #ccc;
`;

const AddMemberRow = styled.td`
	background: #ddd;
	cursor: pointer;
	transition: all 0.3s;
	
	&:hover {
		background: #ccc;
	}
`;


// Define the accepted props
interface Props {
	storybookMembers?: GetMembersResponse;
	storybookIsFormOpen?: true;
	storybookRegistrationSettings?: GetRegistrationResponse;
	storybookCurrentMember?: GetMembersResponse[0];
}


// Function component
const Members: React.FC<Props> = ({
	storybookMembers,
	storybookIsFormOpen,
	storybookRegistrationSettings,
	storybookCurrentMember,
}) => {
	// Use state
	const [members, setMembers] = useState<GetMembersResponse | null>(storybookMembers ?? null);
	const [isFormOpen, setIsFormOpen] = useState(storybookIsFormOpen ?? false);
	const [currentMember, setCurrentMember] = useState<GetMembersResponse[0] | undefined>(storybookCurrentMember);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const signedInMember = useAppSelector((state) => state.self.member);
	
	
	// Use ref
	const ref = useRef<HTMLDivElement | null>(null);
	
	
	// Use React Router functionality
	const updateHeight: (newHeight: number) => void = useOutletContext();
	
	
	// Update height for the members settings page
	useEffect(() => {
		if (updateHeight instanceof Function && ref.current) {
			updateHeight(ref.current.clientHeight);
		}
	}, [updateHeight, members]);
	
	
	// Function that loads members
	const memoizedLoadMembers = useCallback(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetMembersResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/account/members`,
				completion: (json) => {
					setMembers(json.response);
				},
			})
		);
	}, [dispatch]);
	
	
	// Load members on mount
	useEffect(() => {
		memoizedLoadMembers();
	}, [memoizedLoadMembers]);
	
	
	// Handle click
	const handleEditClick = (member: GetMembersResponse[0]) => {
		setCurrentMember(member);
		setIsFormOpen(true);
	};
	
	const handleAddClick = () => {
		setCurrentMember(undefined);
		setIsFormOpen(true);
	};
	
	
	// Handle form completion
	const handleFormCompletion = () => {
		// Close form
		setIsFormOpen(false);
		
		
		// Reload members
		memoizedLoadMembers();
	};
	
	
	// Return JSX
	return (
		<React.Fragment>
			<UpsertMember
				member={currentMember}
				onCompletion={handleFormCompletion}
				onCancel={() => setIsFormOpen(false)}
				storybookRegistrationSettings={storybookRegistrationSettings}
				isFormOpen={isFormOpen}
			/>
			
			<div ref={ref}>
				<Table>
					<thead>
						<tr>
							<th>Member</th>
							<th style={{ textAlign: 'center' }}>Edit</th>
							<th style={{ textAlign: 'center' }}>Delete</th>
						</tr>
					</thead>
					<tbody>
						{members?.map((member) => (
							<tr key={member.id}>
								<td>
									<MemberCell>
										{member.profilePhotoURL ? (
											<Image src={member.profilePhotoURL} alt='' />
										) : (
											<ProfileAvatar firstName={member.firstName} lastName={member.lastName} round />
										)}
										<NameContainer>
											{member.formattedName}
											{member.designation && (
												<small className='is-gray'>
													{' '}
													<em>({member.designation})</em>
												</small>
											)}
											{member.email && (
												<React.Fragment>
													<br />
													<Email className='is-gray'>{member.email}</Email>
												</React.Fragment>
											)}
										</NameContainer>
									</MemberCell>
								</td>
								<td style={{ textAlign: 'center' }}>
									{member.email ? (
										'N/A'
									) : (
										<EditMemberButton
											type='button'
											color='plain'
											aria-label='Edit member'
											smaller
											onClick={() => handleEditClick(member)}
										>
											<FontAwesomeIcon icon={faPencil} />
										</EditMemberButton>
									)}
								</td>
								<td style={{ textAlign: 'center' }}>
									{member.id === signedInMember?.id ? (
										<CannotDeleteContainer>
											<em>
												Cannot delete
												<br />
												yourself
											</em>
										</CannotDeleteContainer>
									) : (
										<APIButton
											options={{
												action: 'DELETE',
												uri: `/website/${window.location.hostname}/account/member/${member.id}`,
												warning: {
													message: 'Deleting this member is permanent.',
													button: `Yes, delete “${member.formattedName}”`,
												},
											}}
											onCompletion={memoizedLoadMembers}
											color='danger'
											aria-label='Delete member'
											smaller
										>
											<FontAwesomeIcon icon={faTimesCircle} />
										</APIButton>
									)}
								</td>
							</tr>
						))}
						
						{members === null ? (
							<LoadingRow />
						) : (
							!isFormOpen && (
								<tr>
									<AddMemberRow
										colSpan={999}
										{...helperFunctions.buttonize(handleAddClick)}
										style={{ textAlign: 'center' }}
									>
										<FontAwesomeIcon icon={faPlus} /> Add member
									</AddMemberRow>
								</tr>
							)
						)}
					</tbody>
				</Table>
			</div>
		</React.Fragment>
	);
};

export default Members;
